/** @jsxImportSource theme-ui */
import classNames from 'classnames';
import React, { useState } from 'react';
import { Dropdown, DropdownItemProps } from 'semantic-ui-react';
import { SxProp, ThemeUIStyleObject } from 'theme-ui';

import theme from 'theme';
import { sxtheme } from 'utils/cssHelpers';

import { BdIcon } from 'components/_common/BdIcon';

const dropdownStyle: ThemeUIStyleObject = {
  height: '40px',
  minHeight: '40px',
  width: '300px',
  backgroundColor: 'white',
  mb: '0',
  '&.ui.dropdown': {
    fontSize: '14px',
    p: '10px',
    borderBottom: `1px solid ${theme.colors.stone20}`,
    '&.outline': {
      p: '14px 16px',
      minHeight: '48px',
      borderBottom: 'unset',
      border: `1px solid ${theme.colors.stone20}`,
      borderRadius: '2px',
      '&.expanded': { border: `1px solid ${theme.colors.deepSea50}` },
    },
    '&.error': {
      border: `1px solid ${theme.colors.clay100}`,
      borderBottom: `1px solid ${theme.colors.clay100}`,
      '.icon': {
        color: sxtheme(t => t.colors.clay100),
      },
    },
    '.menu': {
      mt: '4px',
      py: '12px',
      maxHeight: `calc(50vh - 42px)`,
      overflowY: 'auto',
      minWidth: 'fill-available',
      '& > .header': { opacity: 1, '&:hover': { background: 'inherit' } },
      '& > .header:first-of-type': {
        p: '12px 16px 4px 16px !important',
        textTransform: 'none',
      },
      '& > .header:not(:first-of-type)': {
        p: '24px 16px 4px 16px !important',
        textTransform: 'none',
      },
    },
  },
  '&.ui.fluid.dropdown': { display: 'flex', '& .text': { flexGrow: 1 } },
  '.ui.selection.active.dropdown:hover': { boxShadow: 'none' },
  '.ui.inline.dropdown': {
    padding: '14px 16px',
    width: '100%',
    height: '100%',
    '&.active': { '.menu': { mt: '4px' } },
  },
  '.ui.inline.dropdown > .default.text': { color: theme.colors.stone90 },
  '.ui.inline.dropdown > .text': { fontWeight: 'normal' },
  '.icon': { float: 'right', paddingTop: '2px', verticalAlign: 'text-bottom' },
};

type BdDropdownProps = SxProp & {
  /** An array of options in the dropdown */
  options: DropdownItemProps[];
  /** The active value of the dropdown */
  value: boolean | number | string | (boolean | number | string)[];
  /** A custom react component for any special styling on the closed dropdown */
  trigger?: React.ReactNode;
  /** Placeholder for the input (overridden by trigger) */
  placeholder?: string;
  /** Will highlight the component red if true */
  error?: boolean;
  /** Disables the component */
  disabled?: boolean;
  /** Ensures the component fills full width of parent container */
  fluid?: boolean;
  /** For an inline dropdown */
  inline?: boolean;
  /** The dropdown menu will not be contained by the parent element and not resize it */
  floating?: boolean;
  /** Fully outlines the dropdown component, false if just bottom lined */
  outline?: boolean;
  onChange?: (value: string | number | boolean | (string | number | boolean)[]) => void;
  onBlur?: (value: boolean) => void;
};

export const BdDropdown: React.FC<BdDropdownProps> = ({
  trigger,
  placeholder,
  options,
  value,
  error,
  disabled,
  fluid = false,
  inline = false,
  floating = false,
  outline = false,
  onChange,
  onBlur,
  children,
  ...props
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Dropdown
      {...props}
      data-testid="bd-dropdown"
      fluid={fluid}
      placeholder={placeholder}
      options={options}
      value={value}
      onChange={(_, data) => onChange && onChange(data.value)}
      onBlur={() => onBlur && onBlur(true)}
      disabled={disabled}
      error={error}
      inline={inline}
      icon={
        <BdIcon
          data-testid="dropdown-icon"
          name={isExpanded ? 'icon-chevron-up' : 'icon-chevron-down'}
          color="sea100"
          bold
          nomargin
        />
      }
      trigger={trigger}
      onClose={() => setIsExpanded(false)}
      onOpen={() => setIsExpanded(true)}
      className={classNames({ outline, expanded: isExpanded })}
      sx={dropdownStyle}
    >
      {children}
    </Dropdown>
  );
};
